import { useState } from "react";
import {
  BODY,
  BURN_FRONT,
  BURN_BACK,
  BURN_DIV_FRONT,
  BURN_DIV_BACK,
  type BurnMode,
  type BurnRegion,
} from "@ambulance/shared";

// Rule-of-nines burn chart: blank front + back figures with reference %
// printed on each region (Adult or Pediatric). The percentages are a guide
// only — nothing is calculated; the user enters the total burned area (TBSA)
// manually. Body-map geometry lives in @/lib/burnChart, shared with the static
// figure on the official PCR form.
//
// All visual styling is inline (not via global CSS classes) so the figure can
// never fall back to default black fills regardless of stylesheet load state.
// Each region may own several rectangles so the body tiles with no dead gaps.

type Mode = BurnMode;
type Region = BurnRegion;

const FRONT = BURN_FRONT;
const BACK = BURN_BACK;
const DIV_FRONT = BURN_DIV_FRONT;
const DIV_BACK = BURN_DIV_BACK;

function Figure({
  view,
  regions,
  divs,
  value,
  onToggle,
  mode,
  readOnly,
}: {
  view: "f" | "b";
  regions: Region[];
  divs: string[];
  value: Record<string, boolean>;
  onToggle: (k: string) => void;
  mode: Mode;
  readOnly?: boolean;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const cid = `burnclip_${view}`;
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 240 520" className="w-32 sm:w-36 h-auto">
        <defs>
          <clipPath id={cid}>
            <path d={BODY} />
          </clipPath>
        </defs>
        <path
          d={BODY}
          style={{ fill: "#ffffff", stroke: "#1f2937", strokeWidth: 2, strokeLinejoin: "round" }}
        />
        {regions.map((rg) => {
          const key = `${view}_${rg.id}`;
          const sel = !!value[key];
          const opacity = sel ? 0.66 : !readOnly && hover === key ? 0.2 : 0;
          return rg.rects.map((rc, i) => {
            const [x, y, w, h] = rc;
            return (
              <rect
                key={`${key}_${i}`}
                x={x}
                y={y}
                width={w}
                height={h}
                clipPath={`url(#${cid})`}
                style={{
                  fill: "#c0392b",
                  opacity,
                  pointerEvents: "all",
                  cursor: readOnly ? "default" : "pointer",
                  transition: "opacity 0.12s ease",
                }}
                onClick={readOnly ? undefined : () => onToggle(key)}
                onMouseEnter={readOnly ? undefined : () => setHover(key)}
                onMouseLeave={readOnly ? undefined : () => setHover(null)}
              />
            );
          });
        })}
        {divs.map((d, i) => (
          <path
            key={i}
            d={d}
            clipPath={`url(#${cid})`}
            style={{
              fill: "none",
              stroke: "#64748b",
              strokeWidth: 1.2,
              strokeDasharray: "4 3",
              pointerEvents: "none",
            }}
          />
        ))}
        {regions.map((rg) => (
          <text
            key={rg.id}
            x={rg.lx}
            y={rg.ly}
            textAnchor="middle"
            transform={rg.rot ? `rotate(${rg.rot},${rg.lx},${rg.ly})` : undefined}
            style={{ fill: "#2b2b2b", fontSize: "13px", fontWeight: 500, pointerEvents: "none" }}
          >
            {mode === "pediatric" ? rg.ped : rg.adult}
          </text>
        ))}
      </svg>
      <div className="text-xs font-semibold text-slate-600 mt-1">
        {view === "f" ? "Front" : "Back"}
      </div>
    </div>
  );
}

export function BurnDiagram({
  value,
  onChange,
  mode = "adult",
  onModeChange,
  tbsa = "",
  onTbsaChange,
  readOnly,
}: {
  value: Record<string, boolean>;
  onChange?: (next: Record<string, boolean>) => void;
  mode?: Mode;
  onModeChange?: (m: Mode) => void;
  tbsa?: string;
  onTbsaChange?: (v: string) => void;
  readOnly?: boolean;
}) {
  const toggle = (key: string) => onChange?.({ ...value, [key]: !value[key] });
  const clearAll = () => onChange?.({});
  const anyMarked = Object.values(value).some(Boolean);

  const tab = (m: Mode, label: string) => (
    <button
      type="button"
      onClick={() => onModeChange?.(m)}
      className={`px-3 py-1.5 ${
        mode === m
          ? "bg-[var(--hmg-red)] text-white"
          : "bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Reference chart
        </span>
        {readOnly ? (
          <span className="text-xs font-semibold text-slate-600">
            {mode === "pediatric" ? "Pediatric (child)" : "Adult"}
          </span>
        ) : (
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-xs font-semibold">
            {tab("adult", "Adult")}
            {tab("pediatric", "Pediatric")}
          </div>
        )}
      </div>

      <div className="flex gap-4 sm:gap-8 justify-center">
        <Figure
          view="f"
          regions={FRONT}
          divs={DIV_FRONT}
          value={value}
          onToggle={toggle}
          mode={mode}
          readOnly={readOnly}
        />
        <Figure
          view="b"
          regions={BACK}
          divs={DIV_BACK}
          value={value}
          onToggle={toggle}
          mode={mode}
          readOnly={readOnly}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-200">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Total burned area (TBSA)</span>
          {readOnly ? (
            <span className="text-sm font-bold text-slate-900">
              {tbsa ? `${tbsa}%` : "—"}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={tbsa}
                onChange={(e) => onTbsaChange?.(e.target.value)}
                placeholder="0"
                className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
              />
              <span className="text-sm text-slate-600">%</span>
            </span>
          )}
        </div>
        {!readOnly && anyMarked && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-semibold text-slate-500 hover:text-red-600"
          >
            Clear marks
          </button>
        )}
      </div>

      {!readOnly && (
        <p className="text-[11px] text-slate-400 mt-2">
          The percentages on the figure are a reference guide only — enter the
          total burned area yourself.
        </p>
      )}
    </div>
  );
}
